/**
 * Centralized API client for all HTTP requests
 * Uses axios with interceptors for authentication
 */
import axios from 'axios';

// API base URLs
export const SURVEY_API_URL =
  import.meta.env.VITE_SURVEY_API_URL ||
  'https://mydynasty.ca/ext/api/v1/surveys';
export const CD_API_URL =
  import.meta.env.VITE_CD_API_URL || 'https://mydynasty.ca/cd/api/v1';
export const BASE_API_URL =
  import.meta.env.VITE_API_URL || 'https://mydynasty.ca/api/v1';

/**
 * Main API client for survey-related endpoints
 */
export const api = axios.create({
  baseURL: SURVEY_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * API client for Club Dynasty general endpoints
 */
export const cdApi = axios.create({
  baseURL: CD_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Public endpoints on cdApi that should not include auth token
 */
const CD_API_PUBLIC_ENDPOINTS = ['/masseuses/', '/masseuses'];

/**
 * Request interceptor to add authorization token
 */
const addAuthToken = (config: any) => {
  const adminUserInfo = localStorage.getItem('adminUserInfo');
  if (adminUserInfo) {
    try {
      const { token } = JSON.parse(adminUserInfo);
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = token;
      }
    } catch (error) {
      console.error('Failed to parse adminUserInfo from localStorage:', error);
    }
  }
  return config;
};

/**
 * Request interceptor for cdApi - skips auth for public endpoints
 */
const addAuthTokenCdApi = (config: any) => {
  // Skip auth for public endpoints on cdApi
  const isPublicEndpoint = CD_API_PUBLIC_ENDPOINTS.some((endpoint) =>
    config.url?.includes(endpoint),
  );

  if (isPublicEndpoint) {
    return config;
  }

  return addAuthToken(config);
};

/**
 * Response error interceptor
 */
const handleResponseError = (error: any) => {
  const status = error.response?.status;
  const url = error.config?.url;
  const method = error.config?.method?.toUpperCase();
  const serverMessage =
    error.response?.data?.message ||
    error.response?.data?.error ||
    error.message;

  console.error(
    `%c API Error %c ${method} ${url} %c ${status || 'Network Error'}`,
    'background: #ff4444; color: white; padding: 2px 6px; border-radius: 3px 0 0 3px;',
    'background: #333; color: white; padding: 2px 6px;',
    `background: ${status === 401 ? '#ff8800' : '#cc0000'}; color: white; padding: 2px 6px; border-radius: 0 3px 3px 0;`,
  );
  console.error('Server Response:', error.response?.data);
  console.error('Error Details:', {
    status,
    statusText: error.response?.statusText,
    message: serverMessage,
    url,
    method,
  });

  return Promise.reject(error);
};

// Apply interceptors to both API clients
api.interceptors.request.use(addAuthToken, (error) => Promise.reject(error));
api.interceptors.response.use((response) => response, handleResponseError);

cdApi.interceptors.request.use(addAuthTokenCdApi, (error) =>
  Promise.reject(error),
);
cdApi.interceptors.response.use((response) => response, handleResponseError);

/**
 * Helper function to get auth token for direct use
 */
export const getAuthToken = (): string | null => {
  const adminUserInfo = localStorage.getItem('adminUserInfo');
  if (adminUserInfo) {
    try {
      const { token } = JSON.parse(adminUserInfo);
      return token;
    } catch {
      return null;
    }
  }
  return null;
};

export default api;
